// The change queue's entry contract (BL-2345, ADR 066 — Offline Mode § 4): which online door each
// queued operation replays through, and the payload it carries. Offline writes (BL-2431) build
// entries only through buildQueuedEntry, and the replay engine reads them only through
// readQueuedEntry, so the two sides cannot drift.

/**
 * Every operation the queue replays in order, one per online door (ADR 066 § 4.2–4.4). The op decides
 * the door; the payload never names a route, so a stored entry cannot point a replay anywhere else.
 * A lot's creates and field edits are not here: they are the lot's desired state (BL-2681,
 * `lot-desired-state.ts`), and an earlier build's entries for them are moved there on open.
 */
export const CHANGE_QUEUE_OPS = [
  'lot.move',
  'lot.status',
  'lot.relist',
  'lot.media.add',
  'lot.media.remove',
  // BL-2418: a Save's photo order and Main, after that lot's photo adds and removes.
  'lot.media.order',
  'lot.document.add',
  'lot.document.remove',
  'note.create',
  'note.update',
  'note.delete',
] as const;

/** One queued operation's kind. */
export type ChangeQueueOp = (typeof CHANGE_QUEUE_OPS)[number];

/** A value that survives JSON and the structured clone, as the device store keeps it. */
export type ChangeQueueJson =
  | string
  | number
  | boolean
  | null
  | ChangeQueueJson[]
  | { [key: string]: ChangeQueueJson };

/** The request an entry replays: the door's path params, its query and its JSON body. */
export type ChangeQueueCall = {
  params: Record<string, string>;
  query: Record<string, string> | null;
  body: ChangeQueueJson;
};

/**
 * How the queue names the lot an entry changes (ADR 066 § 8, signed by Michael Lawrie
 * 2026-10-10): captured when the change is queued and shown as written, online and offline. It is
 * the entry's own text, never a read of the cached lot.
 */
export type ChangeQueueLabel = {
  /** The lot number as it read when the change was queued, or null (a new lot has none yet). */
  lotNumber: string | null;
  title: string;
  /** True for a lot created offline, shown as "New lot" until it syncs. */
  newLot: boolean;
};

/** What an entry's payload holds. */
export type ChangeQueuePayload = {
  call: ChangeQueueCall;
  label: ChangeQueueLabel;
  /** The staged file a photo, video or document add uploads first (BL-2421), else null. */
  stagedFileId: string | null;
  /**
   * A video add's poster frame, staged beside the video (BL-2447, BL-2345 AC 10): uploaded before
   * it at replay, so a poster that must wait never leaves an uploaded video behind. Absent on every
   * other entry, and on a video whose frame could not be captured.
   */
  stagedPosterFileId?: string;
  /**
   * The auction a queued move takes the lot out of (BL-2448). The device shows the lot moved at once,
   * so discarding the move re-downloads this auction to bring the lot back to it. Absent on every
   * other entry.
   */
  leftAuctionId?: string;
};

/**
 * The ops whose success mints a server id the entries after them may name: a relist's copy
 * (BL-2448) and its photos, or a new note's edits. The entry's own id is that record's client id
 * until it syncs (its `clientOpId`, BL-2337).
 */
export const CHANGE_QUEUE_CREATE_OPS: ReadonlySet<ChangeQueueOp> = new Set<ChangeQueueOp>(['lot.relist', 'note.create']);

/**
 * The ops whose stale-base refusal (409 `STALE_FIELDS`) can be resent with `overwrite: true`: a
 * lot's photo order (BL-2417, BL-2418). A lot's field edit is a desired state that carries its own
 * Overwrite (BL-2681).
 */
const OVERWRITABLE_OPS: ReadonlySet<ChangeQueueOp> = new Set<ChangeQueueOp>(['lot.media.order']);

/**
 * @param op - a stored entry's op.
 * @returns whether a refusal of it offers, and accepts, Overwrite.
 */
export function isOverwritableOp(op: string): boolean {
  return isChangeQueueOp(op) && OVERWRITABLE_OPS.has(op);
}

/**
 * Whether a stored entry makes a lot that is still in the queue: a relist's copy (BL-2448). Its id
 * is the lot's client id until it syncs, so it names a lot the server has never seen. A lot made by
 * a create is a desired state instead (BL-2681), and a note's create is not a lot.
 *
 * @param entry - a stored entry's op.
 * @returns true for a queued `lot.relist`.
 */
export function isQueuedLotCreate(entry: { op: string }): boolean {
  return entry.op === 'lot.relist';
}

/**
 * @param op - a stored entry's op.
 * @returns whether it is one the queue replays.
 */
export function isChangeQueueOp(op: string): op is ChangeQueueOp {
  return (CHANGE_QUEUE_OPS as readonly string[]).includes(op);
}

/**
 * The group key of every entry that changes one lot, so the queue lists them together and merges
 * the lot's edits. A new lot's key uses its client id.
 *
 * @param lotId - the lot's server id, or a new lot's client id.
 * @returns the key.
 */
export function lotGroupKey(lotId: string): string {
  return `lot:${lotId}`;
}

/**
 * @param groupKey - an entry's group key.
 * @returns the lot id it names, or null when it names no lot.
 */
export function lotIdOfGroupKey(groupKey: string | null): string | null {
  return groupKey?.startsWith('lot:') ? groupKey.slice('lot:'.length) : null;
}

/** What {@link buildQueuedEntry} returns: the outbox append input, store-agnostic. */
export interface QueuedEntryInput {
  id: string;
  op: ChangeQueueOp;
  payload: ChangeQueueJson;
  dependsOn: string | null;
  groupKey: string | null;
}

/**
 * Build one queue entry. A create's `id` doubles as its `clientOpId` (BL-2337); an entry for a lot
 * created offline names the lot by that id and `dependsOn` its create.
 *
 * @param input.id - a client-minted UUID.
 * @param input.op - the door it replays through.
 * @param input.call - the request it replays.
 * @param input.label - how the queue names the lot, captured now.
 * @param input.lotId - the lot it changes, or null.
 * @param input.dependsOn - the entry it waits on, or null.
 * @param input.stagedFileId - the staged file an add uploads first, or null.
 * @param input.stagedPosterFileId - a video add's staged poster frame, or null.
 * @param input.leftAuctionId - the auction a move takes the lot out of, or null.
 * @returns the outbox append input.
 */
export function buildQueuedEntry(input: {
  id: string;
  op: ChangeQueueOp;
  call: ChangeQueueCall;
  label: ChangeQueueLabel;
  lotId: string | null;
  dependsOn?: string | null;
  stagedFileId?: string | null;
  stagedPosterFileId?: string | null;
  leftAuctionId?: string | null;
}): QueuedEntryInput {
  const payload: ChangeQueuePayload = {
    call: input.call,
    label: input.label,
    stagedFileId: input.stagedFileId ?? null,
    ...(input.stagedPosterFileId ? { stagedPosterFileId: input.stagedPosterFileId } : {}),
    ...(input.leftAuctionId ? { leftAuctionId: input.leftAuctionId } : {}),
  };
  return {
    id: input.id,
    op: input.op,
    payload,
    dependsOn: input.dependsOn ?? null,
    groupKey: input.lotId == null ? null : lotGroupKey(input.lotId),
  };
}

// The reasons reach the Change queue tab (BL-2438), so they name no op code or field.
const NEWER_APP = 'This change is from a newer version of the app.';
const UNREADABLE = 'This change could not be read.';

/** Outcome of {@link readQueuedEntry}. */
export type ReadQueuedEntryResult =
  | { ok: true; op: ChangeQueueOp; payload: ChangeQueuePayload }
  | { ok: false; reason: string };

/** A parsed JSON object whose fields are not yet checked. */
export interface JsonObject {
  [key: string]: unknown;
}

/**
 * @param value - anything parsed from JSON or read from the store.
 * @returns whether it is a plain object (not null, not an array).
 */
export function isRecord(value: unknown): value is JsonObject {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isStringMap(value: unknown): value is Record<string, string> {
  return isRecord(value) && Object.values(value).every((v) => typeof v === 'string');
}

/**
 * Read a stored entry back, checking its shape: the store holds whatever an earlier build wrote.
 *
 * @param entry - the stored op and payload.
 * @returns the typed op and payload, or why it cannot be replayed.
 */
export function readQueuedEntry(entry: { op: string; payload: unknown }): ReadQueuedEntryResult {
  if (!isChangeQueueOp(entry.op)) return { ok: false, reason: NEWER_APP };
  const p = entry.payload;
  if (!isRecord(p) || !isRecord(p.call) || !isRecord(p.label)) return { ok: false, reason: UNREADABLE };
  const { call, label } = p;
  if (!isStringMap(call.params) || !(call.query === null || isStringMap(call.query))) {
    return { ok: false, reason: UNREADABLE };
  }
  if (typeof label.title !== 'string' || typeof label.newLot !== 'boolean') return { ok: false, reason: UNREADABLE };
  return {
    ok: true,
    op: entry.op,
    payload: {
      call: { params: call.params, query: call.query, body: (call.body ?? null) as ChangeQueueJson },
      label: {
        lotNumber: typeof label.lotNumber === 'string' ? label.lotNumber : null,
        title: label.title,
        newLot: label.newLot,
      },
      stagedFileId: typeof p.stagedFileId === 'string' ? p.stagedFileId : null,
      ...(typeof p.stagedPosterFileId === 'string' ? { stagedPosterFileId: p.stagedPosterFileId } : {}),
      ...(typeof p.leftAuctionId === 'string' ? { leftAuctionId: p.leftAuctionId } : {}),
    },
  };
}
